import ReportProblemIcon from '@mui/icons-material/ReportProblem'
import { Box, Button, Divider, Stack, Typography } from '@mui/material'
import { useAtomValue } from 'jotai'
import { FC, useCallback, useMemo, useState } from 'react'
import { Link as ReactRouterLink, useNavigate } from 'react-router-dom'
import { addressValidationResultAtom, currentLocationChangeAtom, selectedAddressValidationResultIndexAtom } from '../../../atoms'
import { SELECTED_ADDRESS_UNVERIFIED } from '../../../constants'
import { AddressConfirmationDialog } from './AddressConfirmationDialog'
import { addressValidationPaths } from '../addressValidationPaths'

type AddressConfirmationProps = {
  title?: string
}

const AddressConfirmation: FC<AddressConfirmationProps> = ({ title }) => {
  const navigate = useNavigate()
  const addressValidationResults = useAtomValue(addressValidationResultAtom)
  const selectedAddressValidationResult = useAtomValue(selectedAddressValidationResultIndexAtom)
  const currentLocationChange = useAtomValue(currentLocationChangeAtom)
  const [isConfirmAddressDialogOpen, setIsConfirmAddressDialogOpen] = useState(false)

  const handleConfirm = () => {
    if (selectedAddressValidationResult === SELECTED_ADDRESS_UNVERIFIED) {
      navigate(`../${addressValidationPaths.timeZone}`)
    } else {
      setIsConfirmAddressDialogOpen(true)
    }
  }

  const handleConfirmAddress = () => {
    navigate(`../${addressValidationPaths.datePicker}`)
  }

  const criticalIssues = useMemo(
    () => addressValidationResults?.candidates.flatMap(candidate => candidate.issues.filter(issue => issue.severity === 'Critical')) || [],
    [addressValidationResults]
  )

  const warnings = useMemo(
    () => addressValidationResults?.candidates.flatMap(candidate => candidate.issues.filter(issue => issue.severity === 'Warning')) || [],
    [addressValidationResults]
  )

  const getIssueTitle = useCallback(() => {
    if (selectedAddressValidationResult === SELECTED_ADDRESS_UNVERIFIED) {
      return 'Confirm Address'
    } else if (criticalIssues.length) {
      return 'This address cannot be monitored'
    } else {
      return 'Confirm Address'
    }
  }, [selectedAddressValidationResult, criticalIssues])

  const getIssueDescription = useCallback(() => {
    if (selectedAddressValidationResult === SELECTED_ADDRESS_UNVERIFIED) {
      return "We couldn't verify this address. Review it carefully with the customer before continuing."
    } else if (criticalIssues.length) {
      return criticalIssues[0]?.description.external
    } else if (warnings.length) {
      return warnings.map(issue => issue.description.external).join(' ')
    }
    return null
  }, [selectedAddressValidationResult, criticalIssues, warnings])

  // 1. If selectedAddressValidationResult is SELECTED_ADDRESS_VALIDATION_UNDEFINED: show Edit Address and Continue
  // 2. If not SELECTED_ADDRESS_VALIDATION_UNDEFINED and has warning issues: show Edit Address and Confirm
  // 3. If not SELECTED_ADDRESS_VALIDATION_UNDEFINED and has critical issues: show Exit button only
  const showEditAndConfirmButtons = !!(selectedAddressValidationResult === SELECTED_ADDRESS_UNVERIFIED || (warnings.length && !criticalIssues.length))
  const showExitButton = !!(selectedAddressValidationResult !== SELECTED_ADDRESS_UNVERIFIED && criticalIssues.length)

  return (
    <>
      <Stack flexGrow={1} useFlexGap sx={{ bgcolor: 'var(--bg-surface)', boxShadow: 1, borderRadius: 'var(--border-radius-s)' }}>
        <Stack spacing={'var(--spacing-xl)'} sx={{ padding: 'var(--spacing-m) var(--spacing-l)' }}>
          {title && (
            <Typography fontSize="var(--size-xl)" fontWeight="700">
              {title}
            </Typography>
          )}
          <Stack flexDirection={'column'} alignItems="flex-start" justifyContent="flex-start">
            {selectedAddressValidationResult !== SELECTED_ADDRESS_UNVERIFIED && (
              <ReportProblemIcon color="error" sx={{ width: 'var(--icons-m)', height: 'var(--icons-m)' }} />
            )}
            <Typography fontSize="var(--size-l)" fontWeight="500" data-testid="address-confirmation-issue-message">
              {getIssueTitle()}
            </Typography>
            <Typography data-testid="address-confirmation-issue-message-description">{getIssueDescription()}</Typography>
            <Box sx={{ marginTop: 'var(--spacing-2xs)' }}>
              <Typography fontWeight="700">You entered:</Typography>
              <Typography data-testid="address-confirmation-address-street-1">{currentLocationChange?.address?.street1}</Typography>
              {currentLocationChange?.address?.street2 && (
                <Typography data-testid="address-confirmation-address-street-2">{currentLocationChange?.address?.street2}</Typography>
              )}
              <Typography data-testid="address-confirmation-address-city-state">
                {currentLocationChange?.address?.city}, {currentLocationChange?.address?.state}
              </Typography>
              <Typography data-testid="address-confirmation-address-zip">{currentLocationChange?.address?.postalCode}</Typography>
            </Box>
          </Stack>
        </Stack>

        <Divider />

        <Stack data-testid="address-confirmation-actions" direction="row" justifyContent="flex-end" spacing={2} sx={{ padding: 'var(--spacing-m)' }}>
          {showExitButton && (
            <Button component={ReactRouterLink} to="../../overview" type="button" data-testid="address-confirmation-exit">
              Exit
            </Button>
          )}
          {showEditAndConfirmButtons && (
            <>
              <Button
                component={ReactRouterLink}
                to={`../${addressValidationPaths.addressForm}`}
                type="button"
                data-testid="address-confirmation-edit"
              >
                Edit Address
              </Button>
              <Button type="button" data-testid="address-confirmation-confirm" onClick={handleConfirm}>
                {selectedAddressValidationResult === SELECTED_ADDRESS_UNVERIFIED ? 'Continue' : 'Confirm'}
              </Button>
            </>
          )}
        </Stack>
      </Stack>
      <AddressConfirmationDialog
        isOpen={isConfirmAddressDialogOpen}
        onClose={() => {
          setIsConfirmAddressDialogOpen(false)
        }}
        onConfirm={handleConfirmAddress}
        customerAddress={{
          street1: addressValidationResults?.address?.street1,
          street2: addressValidationResults?.address?.street2,
          city: addressValidationResults?.address?.city,
          state: addressValidationResults?.address?.state,
          zip: addressValidationResults?.address?.zip
        }}
      />
    </>
  )
}

export { AddressConfirmation }
